import React from "react";
import compose from "lodash/fp/compose";
import get from "lodash/get";
import filesize from "filesize";
import gql from "graphql-tag";
import { graphql, withApollo } from "react-apollo";
import { Card, Table, Button, Icon, Modal } from "antd";
import { Form, Input, Popconfirm } from "antd";

import UploadForm from "./UploadForm";

const listingQuery = gql`
  query($path: String!, $licenseId: String) {
    mediaListing(path: $path, licenseId: $licenseId) {
      path
      parent {
        path
      }
      childSet {
        name
        path
      }
      objectSet {
        name
        path
        size
        modified
      }
    }
  }
`;

const objectInfoQuery = gql`
  query($path: String!, $licenseId: String) {
    mediaObjectInfo(path: $path, licenseId: $licenseId) {
      name
      path
      extension
      size
      modified
      isPublic
      publicUrl
    }
  }
`;

const createDirectoryMutation = gql`
  mutation($input: CreateMediaDirectoryInput!) {
    createMediaDirectory(input: $input) {
      path
    }
  }
`;

const deleteDirectoryMutation = gql`
  mutation($input: DeleteMediaDirectoryInput!) {
    deleteMediaDirectory(input: $input) {
      path
    }
  }
`;

const deleteObjectMutation = gql`
  mutation($input: DeleteMediaObjectInput!) {
    deleteMediaObject(input: $input) {
      path
    }
  }
`;

const generateMediaUploadUrlMutation = gql`
  mutation($input: GenerateMediaUploadUrlInput!) {
    generateMediaUploadUrl(input: $input) {
      url
    }
  }
`;

const formatDate = (dateString) => dateString.replace(/[TZ]/g, " ").trim();

class WrappedMediaListing extends React.Component {
  state = {
    createDirectory: undefined,
    upload: undefined,
  };

  handleCreateDirectoryRequested = () => this.setState({ createDirectory: "" });
  handleCreateDirectoryChanged = (e) => {
    const directory = e.target.value.replace(/[^\w\s-]/g, "");
    this.setState({ createDirectory: directory });
  };
  handleCreateDirectoryCancelled = () =>
    this.setState({ createDirectory: undefined });
  handleCreateDirectorySubmitted = async () => {
    const {
      listingQuery,
      createDirectoryMutation,
      path,
      licenseId,
    } = this.props;
    const { createDirectory: name } = this.state;
    const variables = { input: { prefix: path, name, licenseId } };
    await createDirectoryMutation({ variables });
    await listingQuery.refetch();
    this.setState({ createDirectory: undefined });
  };

  handleDeleteDirectoryConfirmed = async (path) => {
    const { listingQuery, deleteDirectoryMutation, licenseId } = this.props;
    const variables = { input: { path, licenseId } };
    await deleteDirectoryMutation({ variables });
    await listingQuery.refetch();
  };

  handleGenerateUploadUrlRequested = async ({ file }) => {
    const { generateMediaUploadUrlMutation, licenseId, path } = this.props;
    const input = {
      prefix: path,
      name: file.name,
      ...(licenseId && { licenseId }),
    };
    const variables = { input };
    const result = await generateMediaUploadUrlMutation({ variables });
    const url = get(result, "data.generateMediaUploadUrl.url");
    return url;
  };

  handleUploadRequested = () => this.setState({ upload: new Date() });
  handleUploadCancelled = () => this.setState({ upload: undefined });
  handleUploadHappened = async () => {
    await this.props.listingQuery.refetch();
  };
  handleUploadFinished = async () => {
    await this.props.listingQuery.refetch();
    this.setState({ upload: undefined });
  };

  handleDeleteObjectConfirmed = async (path) => {
    const { listingQuery, deleteObjectMutation, licenseId } = this.props;
    const variables = { input: { path, licenseId } };
    await deleteObjectMutation({ variables });
    await listingQuery.refetch();
  };

  handleObjectInfoRequested = async (path) => {
    const { client, licenseId } = this.props;
    const {
      data: { mediaObjectInfo },
    } = await client.query({
      query: objectInfoQuery,
      variables: { path, licenseId },
    });
    this.setState({ objectInfo: mediaObjectInfo });
  };
  handleObjectInfoCancelled = () => this.setState({ objectInfo: undefined });

  handleObjectSelection = () => {
    const { onObjectChosen } = this.props;
    const { objectInfo } = this.state;
    onObjectChosen(objectInfo);
    this.setState({ objectInfo: undefined });
  };

  render() {
    const { listingQuery, onPathChangeRequested } = this.props;

    const { createDirectory, upload, objectInfo } = this.state;

    return (
      <React.Fragment>
        {(() => {
          const { loading, mediaListing, error, refetch } = listingQuery;

          if (loading) {
            return <p>Loading ...</p>;
          }

          if (error || !mediaListing) {
            return (
              <p>
                Something went wrong ...{" "}
                <Button onClick={() => refetch()}>Try again</Button>
              </p>
            );
          }

          const { path, parent, childSet, objectSet } = mediaListing;

          const pathSegments = !!path ? ["", ...path.split("/")] : [""];
          const pathNavigation = pathSegments.reduce(
            ({ carry, items }, segment) => {
              const path = [...carry, segment];
              const item = { path: path.join("/"), segment };
              return { carry: path, items: [...items, item] };
            },
            { carry: [], items: [] }
          ).items;

          const listingItems = [];
          if (path) {
            listingItems.push({
              iconType: "folder",
              name: "..",
              path: parent.path,
              onClick: () => onPathChangeRequested(parent.path),
            });
          }
          childSet.forEach(({ name, path }) => {
            listingItems.push({
              iconType: "folder",
              name,
              path,
              onClick: () => onPathChangeRequested(path),
              onDelete: () => this.handleDeleteDirectoryConfirmed(path),
            });
          });
          objectSet.forEach(({ name, path, size, modified }) => {
            listingItems.push({
              iconType: "file",
              name,
              path,
              size,
              modified,
              onClick: () => this.handleObjectInfoRequested(path),
              onDelete: () => this.handleDeleteObjectConfirmed(path),
            });
          });

          return (
            <Card
              title={
                <Button.Group>
                  {pathNavigation.map(({ path, segment }, index) => (
                    <Button
                      key={path}
                      onClick={() => onPathChangeRequested(path)}
                      size="small"
                      disabled={index === pathNavigation.length - 1}
                    >
                      {segment !== "" ? segment : "/"}
                    </Button>
                  ))}
                </Button.Group>
              }
              extra={
                <Button.Group>
                  <Button
                    onClick={this.handleCreateDirectoryRequested}
                    size="small"
                  >
                    <Icon type="folder-add" />
                  </Button>
                  <Button onClick={this.handleUploadRequested} size="small">
                    <Icon type="upload" />
                  </Button>
                  <Button onClick={() => refetch()} size="small">
                    <Icon type="reload" />
                  </Button>
                </Button.Group>
              }
              size="small"
            >
              <Table
                columns={[
                  {
                    title: "Name",
                    dataIndex: "name",
                    render: (text, { onClick, iconType }, index) => (
                      <Button type="link" size="small" onClick={onClick}>
                        <Icon type={iconType} /> {text}
                      </Button>
                    ),
                  },
                  {
                    title: "Size",
                    dataIndex: "size",
                    render: (text, { size }, index) => size && filesize(size),
                  },
                  {
                    title: "Modified",
                    dataIndex: "modified",
                    render: (text, { modified }, index) =>
                      modified && formatDate(modified),
                  },
                  {
                    title: "Actions",
                    render: (_, { onDelete }, index) =>
                      onDelete && (
                        <Popconfirm
                          title="Do you really want to delete this?"
                          onConfirm={onDelete}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button size="small" type="danger">
                            <Icon type="delete" />
                          </Button>
                        </Popconfirm>
                      ),
                  },
                ]}
                rowKey="path"
                dataSource={listingItems}
                pagination={false}
              />
            </Card>
          );
        })()}

        <Modal
          title="Create a new directory"
          visible={createDirectory !== undefined}
          onOk={this.handleCreateDirectorySubmitted}
          onCancel={this.handleCreateDirectoryCancelled}
          footer={[
            <Button key="back" onClick={this.handleCreateDirectoryCancelled}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={this.handleCreateDirectorySubmitted}
              disabled={!createDirectory}
            >
              Submit
            </Button>,
          ]}
        >
          <Form.Item
            label="Please enter a name for the directory"
            help="Only alphanumeric characters, underscores, dashes and spaces are allowed"
          >
            <Input
              onChange={this.handleCreateDirectoryChanged}
              value={createDirectory}
            />
          </Form.Item>
        </Modal>

        <Modal
          visible={!!upload}
          title="Select media"
          onCancel={this.handleUploadCancelled}
          footer={null}
          width="70%"
        >
          <UploadForm
            key={upload}
            onGenerateUploadUrl={this.handleGenerateUploadUrlRequested}
            onCancel={this.handleUploadCancelled}
            onHappened={this.handleUploadHappened}
            onFinished={this.handleUploadFinished}
          />
        </Modal>

        <Modal
          visible={!!objectInfo}
          title="Media information"
          onCancel={this.handleObjectInfoCancelled}
          footer={[
            <Button
              key="use"
              onClick={this.handleObjectSelection}
              disabled={!get(objectInfo, "publicUrl")}
            >
              Use
            </Button>,
            <Button
              key="close"
              onClick={this.handleObjectInfoCancelled}
              type="primary"
            >
              Close
            </Button>,
          ]}
          width="70%"
        >
          {objectInfo && (
            <table className="c-media-object-info">
              <tr>
                <th>Name</th>
                <td>{objectInfo.name}</td>
              </tr>
              <tr>
                <th>Extension</th>
                <td>{objectInfo.extension}</td>
              </tr>
              <tr>
                <th>Size</th>
                <td>{filesize(objectInfo.size)}</td>
              </tr>
              <tr>
                <th>Modified</th>
                <td>{formatDate(objectInfo.modified)}</td>
              </tr>
              <tr>
                <th>Is public?</th>
                <td>
                  <Icon
                    type={objectInfo.isPublic ? "check-circle" : "close-circle"}
                  />
                </td>
              </tr>
              <tr>
                <th>Public url</th>
                <td>
                  {objectInfo.publicUrl && (
                    <span>
                      {objectInfo.publicUrl}{" "}
                      <a
                        href={objectInfo.publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon type="link" />
                      </a>
                    </span>
                  )}
                </td>
              </tr>
            </table>
          )}
        </Modal>
      </React.Fragment>
    );
  }
}

const MediaListing = compose(
  graphql(listingQuery, {
    name: "listingQuery",
    options: ({ path, licenseId }) => ({
      variables: { path, licenseId },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }),
  }),
  graphql(createDirectoryMutation, { name: "createDirectoryMutation" }),
  graphql(deleteDirectoryMutation, { name: "deleteDirectoryMutation" }),
  graphql(deleteObjectMutation, { name: "deleteObjectMutation" }),
  graphql(generateMediaUploadUrlMutation, {
    name: "generateMediaUploadUrlMutation",
  }),
  withApollo
)(WrappedMediaListing);

export default MediaListing;
